"use client";

import Image from "@/app/components/ProtectedImage";
import { useState, useEffect, useCallback, useMemo } from "react";
import { useTranslations } from "@/i18n/TranslationProvider";

type SlideContent = {
  title: string;
  subtitle: string;
  description: string;
};

export default function HeroSection({ heroImages }: { heroImages: string[] }) {
  const t = useTranslations();
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [nextSlideIndex, setNextSlideIndex] = useState<number | null>(null);

  const slides = useMemo(() => {
    return heroImages.map((imagePath, index) => {
      const content: readonly SlideContent[] = t.hero.slides;
      return {
        src: imagePath,
        title: content[index]?.title ?? content[0]?.title ?? "",
        subtitle: content[index]?.subtitle ?? content[0]?.subtitle ?? "",
        description: content[index]?.description ?? content[0]?.description ?? "",
      };
    });
  }, [heroImages, t.hero.slides]);

  const transitionTo = useCallback(
    (newIndex: number) => {
      if (slides.length === 0 || isTransitioning) return;
      setIsTransitioning(true);
      setNextSlideIndex(newIndex);
      setTimeout(() => {
        setCurrentSlide(newIndex);
        setNextSlideIndex(null);
        setIsTransitioning(false);
      }, 500);
    },
    [slides.length, isTransitioning]
  );

  useEffect(() => {
    if (slides.length <= 1) return;
    const interval = setInterval(() => {
      const next = (currentSlide + 1) % slides.length;
      transitionTo(next);
    }, 8000);
    return () => clearInterval(interval);
  }, [slides.length, currentSlide, transitionTo]);

  const nextSlide = useCallback(() => {
    const next = (currentSlide + 1) % slides.length;
    transitionTo(next);
  }, [currentSlide, slides.length, transitionTo]);

  const prevSlide = useCallback(() => {
    const prev = (currentSlide - 1 + slides.length) % slides.length;
    transitionTo(prev);
  }, [currentSlide, slides.length, transitionTo]);

  const goToSlide = useCallback(
    (index: number) => {
      if (index !== currentSlide) {
        transitionTo(index);
      }
    },
    [currentSlide, transitionTo]
  );

  const currentSlideData = slides[currentSlide];

  // The back layer mounts the next slide at zero opacity so its image loads before the crossfade.
  const upcomingIndex =
    slides.length > 1 ? (currentSlide + 1) % slides.length : null;
  const backLayerIndex = nextSlideIndex ?? upcomingIndex;
  const nextSlideData =
    backLayerIndex !== null && backLayerIndex !== currentSlide
      ? slides[backLayerIndex]
      : null;

  if (slides.length === 0) {
    return (
      <section className="relative w-full aspect-[16/9] sm:aspect-[16/7] md:aspect-[1920/780] max-h-[45vh] md:max-h-[60vh] overflow-hidden bg-gray-900">
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="w-8 h-8 border-2 border-white border-t-transparent rounded-full animate-spin" />
        </div>
      </section>
    );
  }

  return (
    <section className="relative w-full aspect-[16/9] sm:aspect-[16/7] md:aspect-[1920/780] max-h-[45vh] md:max-h-[60vh] overflow-hidden bg-gray-900">
      <div
        className={`absolute inset-0 transition-opacity duration-500 ${isTransitioning ? "opacity-0" : "opacity-100"
          }`}
        style={{ zIndex: 2 }}
      >
        <Image
          src={currentSlideData.src}
          alt={currentSlideData.title}
          fill
          className="object-cover"
          priority={currentSlide === 0}
          quality={85}
          sizes="100vw"
        />
      </div>

      {nextSlideData && (
        <div
          className={`absolute inset-0 transition-opacity duration-500 ${isTransitioning ? "opacity-100" : "opacity-0"
            }`}
          style={{ zIndex: 1 }}
        >
          <Image
            src={nextSlideData.src}
            alt={nextSlideData.title}
            fill
            className="object-cover"
            quality={85}
            sizes="100vw"
          />
        </div>
      )}

      <div
        className="absolute inset-0 bg-black/25 sm:bg-black/30 md:bg-black/40"
        style={{ zIndex: 3 }}
      />

      <div
        className="absolute inset-0 flex items-center justify-center text-white text-center"
        style={{ zIndex: 4 }}
      >
        <div className="max-w-4xl px-8 sm:px-4">
          <h1
            className={`text-xl sm:text-3xl md:text-4xl lg:text-6xl font-bold mb-2 md:mb-4 drop-shadow-lg leading-tight sm:leading-normal tracking-normal transition-opacity duration-300 ${isTransitioning ? "opacity-0" : "opacity-100"
              }`}
          >
            {currentSlideData.title}
          </h1>
          <h2
            className={`text-base sm:text-xl md:text-2xl lg:text-4xl mb-3 md:mb-6 drop-shadow-lg leading-snug sm:leading-normal tracking-normal transition-opacity duration-300 delay-75 ${isTransitioning ? "opacity-0" : "opacity-100"
              }`}
          >
            {currentSlideData.subtitle}
          </h2>
          <p
            className={`text-sm sm:text-lg md:text-xl lg:text-2xl drop-shadow-lg leading-snug sm:leading-normal tracking-normal transition-opacity duration-300 delay-100 ${isTransitioning ? "opacity-0" : "opacity-100"
              }`}
          >
            {currentSlideData.description}
          </p>
        </div>
      </div>

      <button
        onClick={prevSlide}
        disabled={isTransitioning}
        className="absolute left-2 md:left-6 top-1/2 transform -translate-y-1/2 bg-black hover:bg-gray-800 text-white p-2 md:p-4 rounded-full transition-all duration-300 shadow-lg hover:shadow-xl active:scale-95 touch-manipulation disabled:opacity-50"
        style={{ zIndex: 10 }}
        aria-label="Previous slide"
      >
        <svg
          className="w-4 h-4 md:w-6 md:h-6"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={3}
            d="M15 19l-7-7 7-7"
          />
        </svg>
      </button>
      <button
        onClick={nextSlide}
        disabled={isTransitioning}
        className="absolute right-2 md:right-6 top-1/2 transform -translate-y-1/2 bg-black hover:bg-gray-800 text-white p-2 md:p-4 rounded-full transition-all duration-300 shadow-lg hover:shadow-xl active:scale-95 touch-manipulation disabled:opacity-50"
        style={{ zIndex: 10 }}
        aria-label="Next slide"
      >
        <svg
          className="w-4 h-4 md:w-6 md:h-6"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={3}
            d="M9 5l7 7-7 7"
          />
        </svg>
      </button>

      {slides.length > 1 && (
        <div
          className="absolute bottom-4 md:bottom-8 left-1/2 transform -translate-x-1/2 flex space-x-2 md:space-x-3"
          style={{ zIndex: 10 }}
        >
          {slides.map((_, index) => (
            <button
              key={index}
              onClick={() => goToSlide(index)}
              disabled={isTransitioning}
              className={`w-5 h-5 md:w-4 md:h-4 rounded-full transition-all duration-300 border-2 border-white touch-manipulation disabled:cursor-not-allowed ${index === currentSlide
                  ? "bg-black scale-110 shadow-lg"
                  : "bg-white bg-opacity-50 hover:bg-opacity-75 hover:scale-105"
                }`}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>
      )}
    </section>
  );
}
